import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import Landing from '@/pages/public/Landing';
import NotFound from '@/pages/public/NotFound';
import ClubsList from '@/pages/public/clubs/ClubsList';
import ClubDetail from '@/pages/public/clubs/ClubDetail';
import EventsList from '@/pages/public/events/EventsList';
import EventDetail from '@/pages/public/events/EventDetail';
import WatchPage from '@/pages/public/events/WatchPage';
import Login from '@/pages/public/auth/Login';
import Register from '@/pages/public/auth/Register';
import ForgotPassword from '@/pages/public/auth/ForgotPassword';
import ResetPassword from '@/pages/public/auth/ResetPassword';
import Dashboard from '@/pages/private/Dashboard';
import AccountSettings from '@/pages/private/AccountSettings';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsersList from '@/pages/admin/AdminUsersList';
import AdminUserDetail from '@/pages/admin/AdminUserDetail';
import AdminSports from '@/pages/admin/AdminSports';
import AdminClubs from '@/pages/admin/AdminClubs';
import AdminTeams from '@/pages/admin/AdminTeams';
import AdminRegions from '@/pages/admin/AdminRegions';
import AdminEvents from '@/pages/admin/AdminEvents';
import AdminAuditLogs from '@/pages/admin/AdminAuditLogs';
// Fase 05A — Monetización
import PlansList from '@/pages/public/plans/PlansList';
import CheckoutSession from '@/pages/public/checkout/CheckoutSession';
import {
  CheckoutSuccess,
  CheckoutError,
  CheckoutPending,
} from '@/pages/public/checkout/CheckoutOutcomes';
import AdminPlans from '@/pages/admin/AdminPlans';
import AdminPurchases from '@/pages/admin/AdminPurchases';
import AdminPayments from '@/pages/admin/AdminPayments';
import AdminSubscriptions from '@/pages/admin/AdminSubscriptions';
import AdminEntitlements from '@/pages/admin/AdminEntitlements';
import AdminRevenueShare from '@/pages/admin/AdminRevenueShare';
import AdminDownloadProducts from '@/pages/admin/AdminDownloadProducts';
import AdminVideos from '@/pages/admin/AdminVideos';
import AdminVideoProviders from '@/pages/admin/AdminVideoProviders';
import AdminEventContents from '@/pages/admin/AdminEventContents';
import AdminPlayerProfilesList from '@/pages/admin/AdminPlayerProfilesList';
import AdminPlayerProfileEditor from '@/pages/admin/AdminPlayerProfileEditor';
import AdminPlayerProfilesImport from '@/pages/admin/AdminPlayerProfilesImport';
import AdminPlayerLinkSuggestions from '@/pages/admin/AdminPlayerLinkSuggestions';
import AdminTeamDirectory from '@/pages/admin/AdminTeamDirectory';
import AdminPlayerIntelligenceImport from '@/pages/admin/AdminPlayerIntelligenceImport';
import TeamRosterPublic from '@/pages/public/clubs/TeamRosterPublic';
import PlayerPublicProfile from '@/pages/public/clubs/PlayerPublicProfile';
import PlayerMatchAnalysis from '@/pages/public/players/PlayerMatchAnalysis';
import {
  AdminPlansPage,
  AdminPaymentsPage,
  AdminSettingsPage,
} from '@/pages/admin/placeholders';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';
import { ROUTES } from '@/lib/constants';
import { Toaster } from '@/components/ui/sonner';

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Públicas */}
            <Route path={ROUTES.home} element={<Landing />} />
            <Route path={ROUTES.events} element={<EventsList />} />
            <Route path="/eventos/:slug" element={<EventDetail />} />
            <Route path="/eventos/:slug/watch" element={<WatchPage />} />
            <Route path={ROUTES.clubs} element={<ClubsList />} />
            <Route path="/clubes/:slug" element={<ClubDetail />} />
            {/* Fase 05A — Planes + Checkout */}
            <Route path="/planes" element={<PlansList />} />
            <Route path="/checkout/sesion/:id" element={<ProtectedRoute><CheckoutSession /></ProtectedRoute>} />
            <Route path="/checkout/exito" element={<CheckoutSuccess />} />
            <Route path="/checkout/error" element={<CheckoutError />} />
            <Route path="/checkout/pendiente" element={<CheckoutPending />} />
            <Route path={ROUTES.login} element={<Login />} />
            <Route path={ROUTES.register} element={<Register />} />
            <Route path={ROUTES.forgot} element={<ForgotPassword />} />
            <Route path={ROUTES.reset} element={<ResetPassword />} />

            {/* Privadas */}
            <Route path={ROUTES.dashboard} element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path={ROUTES.account} element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />

            {/* Admin */}
            <Route path={ROUTES.admin} element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path={ROUTES.adminUsers} element={<AdminRoute><AdminUsersList /></AdminRoute>} />
            <Route path="/admin/usuarios/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
            {/* Placeholders fase 01B */}
            <Route path="/admin/deportes" element={<AdminRoute><AdminSports /></AdminRoute>} />
            <Route path="/admin/territorios" element={<AdminRoute><AdminRegions /></AdminRoute>} />
            <Route path="/admin/clubes" element={<AdminRoute><AdminClubs /></AdminRoute>} />
            <Route path="/admin/equipos" element={<AdminRoute><AdminTeams /></AdminRoute>} />
            <Route path="/admin/eventos" element={<AdminRoute><AdminEvents /></AdminRoute>} />
            <Route path="/admin/eventos/:eventId/contenidos" element={<AdminRoute><AdminEventContents /></AdminRoute>} />
            <Route path="/admin/auditoria" element={<AdminRoute><AdminAuditLogs /></AdminRoute>} />
            {/* Fase 08 — Fichas de jugador (rutas estáticas antes que :id) */}
            <Route path="/admin/sugerencias-vinculacion" element={<AdminRoute><AdminPlayerLinkSuggestions /></AdminRoute>} />
            <Route path="/admin/fichas-jugadores" element={<AdminRoute><AdminPlayerProfilesList /></AdminRoute>} />
            <Route path="/admin/fichas-jugadores/importar" element={<AdminRoute><AdminPlayerProfilesImport /></AdminRoute>} />
            <Route path="/admin/fichas-jugadores/nueva" element={<AdminRoute><AdminPlayerProfileEditor /></AdminRoute>} />
            <Route path="/admin/fichas-jugadores/:id" element={<AdminRoute><AdminPlayerProfileEditor /></AdminRoute>} />

            {/* Fase 3B — Player Intelligence · Importador Admin */}
            <Route
              path="/admin/player-intelligence/importar"
              element={<AdminRoute><AdminPlayerIntelligenceImport /></AdminRoute>}
            />
            {/* Fase 08B — Directorio admin por equipo */}
            <Route path="/admin/clubes/:clubId/equipos/:teamId/directorio" element={<AdminRoute><AdminTeamDirectory /></AdminRoute>} />
            {/* Fase 08B — Públicas: plantillas y ficha pública */}
            <Route path="/clubes/:clubSlug/equipos/:teamSlug" element={<TeamRosterPublic />} />
            <Route path="/clubes/:clubSlug/equipos/:teamSlug/jugadores/:playerSlug" element={<PlayerPublicProfile />} />
            {/* Player Intelligence — Análisis completo (Fase 2, fixture-based). */}
            <Route path="/jugadores/:playerId/analisis/:matchId" element={<PlayerMatchAnalysis />} />
            {/* Fase 05A — Monetización admin */}
            <Route path="/admin/planes" element={<AdminRoute><AdminPlans /></AdminRoute>} />
            <Route path="/admin/compras" element={<AdminRoute><AdminPurchases /></AdminRoute>} />
            <Route path="/admin/pagos" element={<AdminRoute><AdminPayments /></AdminRoute>} />
            <Route path="/admin/suscripciones" element={<AdminRoute><AdminSubscriptions /></AdminRoute>} />
            <Route path="/admin/accesos" element={<AdminRoute><AdminEntitlements /></AdminRoute>} />
            <Route path="/admin/revenue-share" element={<AdminRoute><AdminRevenueShare /></AdminRoute>} />
            <Route path="/admin/descargas" element={<AdminRoute><AdminDownloadProducts /></AdminRoute>} />
            <Route path="/admin/videos" element={<AdminRoute><AdminVideos /></AdminRoute>} />
            <Route path="/admin/video-providers" element={<AdminRoute><AdminVideoProviders /></AdminRoute>} />
            <Route path="/admin/configuracion" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />

            {/* Legales placeholder */}
            <Route path={ROUTES.legal} element={<NotFound />} />
            <Route path={ROUTES.privacy} element={<NotFound />} />
            <Route path={ROUTES.cookies} element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
